// Contexto de autenticação com Firebase

import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  loginWithGoogle,
  logout as firebaseLogout,
  onAuthChanged,
  processRedirectResult,
  getStoredGooglePhone,
  clearStoredGooglePhone,
} from '@/services/firebase';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Processar redirect de login (telefone Google) e monitorar auth
  useEffect(() => {
    let unsubscribe = () => {};

    const init = async () => {
      await processRedirectResult();
      unsubscribe = onAuthChanged((firebaseUser) => {
        if (firebaseUser) {
          const phoneNumber = getStoredGooglePhone(firebaseUser.uid) || null;
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            phoneNumber,
          });
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
        setLoading(false);
      });
    };

    init();
    return () => unsubscribe();
  }, []);

  const login = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      const firebaseUser = await loginWithGoogle();
      // Em ambientes com popup (desktop), loginWithGoogle retorna o usuário.
      // Em ambientes com redirect (mobile/webview), o retorno pode ser null
      // e o onAuthStateChanged cuidará de atualizar o estado após o redirect.
      if (firebaseUser) {
        setUser(firebaseUser);
        setIsAuthenticated(true);
      }
    } catch (err) {
      setError(err.message);
      console.error('Erro ao fazer login:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      const uid = user?.uid;
      await firebaseLogout();
      if (uid) clearStoredGooglePhone(uid);
      setUser(null);
      setIsAuthenticated(false);
      setWallet(null);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao fazer logout:', err);
      throw err;
    }
  };

  const connectWallet = (walletData) => {
    setWallet(walletData);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      wallet,
      loading,
      error,
      login,
      logout,
      connectWallet,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
}
